import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useGlobalSearch, SEARCH_MIN_CHARS } from '@/hooks/useGlobalSearch'
import { TopbarSearchIcon } from '@/components/icons/topbar'
import type { SearchEntityType, SearchGroup } from '@/services/search'
import calendarIcon from '@/assets/search/calendar.svg'
import checkSquareIcon from '@/assets/search/check-square.svg'
import creditCardIcon from '@/assets/search/credit-card.svg'
import fileIcon from '@/assets/search/file.svg'
import fileTextIcon from '@/assets/search/file-text.svg'
import toolIcon from '@/assets/search/tool.svg'
import usersIcon from '@/assets/search/users.svg'

const RESULT_SECTIONS: { key: string; types: SearchEntityType[] }[] = [
  { key: 'clients', types: ['clients'] },
  { key: 'jobs', types: ['jobs'] },
  { key: 'technicians', types: ['technicians'] },
  {
    key: 'documents',
    types: ['quotes', 'invoices', 'credit_notes', 'contracts', 'payments'],
  },
  { key: 'tasks', types: ['tasks'] },
]

const RESULT_ICON: Record<
  SearchEntityType,
  { src: string; background: string }
> = {
  clients: { src: usersIcon, background: 'bg-[#fae1fa]' },
  jobs: { src: calendarIcon, background: 'bg-[#fff1ea]' },
  quotes: { src: fileTextIcon, background: 'bg-[#fef3c7]' },
  invoices: { src: creditCardIcon, background: 'bg-[#d1fae5]' },
  credit_notes: { src: creditCardIcon, background: 'bg-[#d1fae5]' },
  contracts: { src: fileIcon, background: 'bg-[#ffe7ba]' },
  technicians: { src: toolIcon, background: 'bg-[#b5f5ec]' },
  tasks: { src: checkSquareIcon, background: 'bg-[#e0f2fe]' },
  payments: { src: creditCardIcon, background: 'bg-[#d1fae5]' },
}

function SearchResults({
  term,
  debounced,
  enabled,
  isError,
  isFetching,
  groups,
  onSelect,
}: {
  term: string
  debounced: string
  enabled: boolean
  isError: boolean
  isFetching: boolean
  groups: SearchGroup[]
  onSelect: (ref: string) => void
}) {
  const { t } = useTranslation('shell')
  if (term.trim().length === 0) return null

  const sections = RESULT_SECTIONS.map(({ key, types }) => ({
    key,
    items: types.flatMap(type =>
      (groups.find(group => group.entity_type === type)?.results ?? []).map(
        result => ({ ...result, entityType: type })
      )
    ),
  })).filter(section => section.items.length > 0)

  if (
    enabled &&
    !isError &&
    !isFetching &&
    debounced === term.trim() &&
    sections.length === 0
  ) {
    return (
      <p className='flex min-h-[54px] w-full items-center justify-center text-center text-sm font-regular leading-[1.4] text-[#3d434e]'>
        <span className='block max-w-full truncate'>
          {t('search.noResults', { term: term.trim() })}
        </span>
      </p>
    )
  }

  return (
    <div className='flex flex-col gap-3'>
      {!enabled && (
        <p className='text-sm text-[#3d434e]'>
          {t('search.minChars', { count: SEARCH_MIN_CHARS })}
        </p>
      )}
      {enabled && isError && (
        <p className='text-sm text-destructive'>{t('search.error')}</p>
      )}
      {enabled &&
        !isError &&
        sections.map(section => (
          <div key={section.key} className='flex flex-col gap-2'>
            <p className='text-sm font-medium uppercase leading-[1.4] text-[#3d434e]'>
              {t(`search.groups.${section.key}`)}
            </p>
            <div className='flex flex-col gap-1'>
              {section.items.map(result => {
                const icon = RESULT_ICON[result.entityType]
                return (
                  <button
                    key={`${result.entityType}-${result.id}`}
                    type='button'
                    onClick={() =>
                      onSelect(
                        result.entityType === 'jobs'
                          ? `/calendar?job=${encodeURIComponent(result.id)}`
                          : result.ref
                      )
                    }
                    className='flex w-full min-w-0 items-center gap-2 rounded-lg p-1 text-left hover:bg-[#fff1ea] focus-visible:bg-[#fff1ea] focus-visible:outline-none'
                  >
                    <span
                      className={cn(
                        'flex size-8 shrink-0 items-center justify-center rounded-xl',
                        icon.background
                      )}
                    >
                      <img src={icon.src} alt='' className='size-4' />
                    </span>
                    <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                      <span className='truncate text-sm font-medium leading-[1.4] text-[#171a20]'>
                        {result.label}
                      </span>
                      {result.subtitle && (
                        <span className='truncate text-xs leading-[1.4] text-[#3d434e]'>
                          {result.subtitle}
                        </span>
                      )}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        ))}
    </div>
  )
}

// Topbar global search (2.13.1 / Figma 863:37091). Always-visible field —
// no icon-expand interaction. Debounced cross-entity search with a results
// dropdown grouped into display sections.
export function GlobalSearch() {
  const { key } = useLocation()
  return <SearchField key={key} />
}

function SearchField() {
  const { t } = useTranslation('shell')
  const navigate = useNavigate()
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { data, isFetching, isError, enabled, debounced } =
    useGlobalSearch(term)

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setTerm('')
        setOpen(false)
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setTerm('')
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const groups = data?.data?.groups ?? []

  function go(ref: string) {
    setOpen(false)
    setTerm('')
    navigate(ref)
  }

  function clear() {
    setTerm('')
    setOpen(false)
    inputRef.current?.focus()
  }

  return (
    <div ref={containerRef} className='relative w-[320px] shrink-0'>
      <div
        className={cn(
          'relative flex h-10 w-full items-center gap-2 overflow-hidden rounded-xl border bg-[linear-gradient(90deg,#ffffff_0%,#fffcfa_30%,#fff8f4_65%,#fff3ec_100%)] px-4 focus-within:border-[#ff5a1f]',
          term.length > 0 ? 'border-[#ff5a1f]' : 'border-[#e7e9ef]'
        )}
      >
        <TopbarSearchIcon className='pointer-events-none size-4 shrink-0 text-[#272c36]' />
        <input
          ref={inputRef}
          type='search'
          value={term}
          onChange={e => {
            setTerm(e.target.value)
            setOpen(true)
          }}
          onFocus={() => {
            if (term.trim().length >= SEARCH_MIN_CHARS) setOpen(true)
          }}
          placeholder={t('search.placeholder')}
          aria-label={t('search.placeholder')}
          aria-busy={isFetching}
          className={cn(
            'min-w-0 flex-1 appearance-none bg-transparent p-0 font-sans text-sm font-normal leading-none text-[#171a20]',
            term.length > 0 && 'leading-[1.4]',
            'placeholder:text-[#3d434e] outline-none',
            '[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden'
          )}
        />
        {term.length > 0 && (
          <button
            type='button'
            onClick={clear}
            aria-label={t('search.clear')}
            className='flex size-4 shrink-0 items-center justify-center text-[#272c36]'
          >
            <X className='size-4' strokeWidth={1.6} />
          </button>
        )}
      </div>

      {open && term.trim().length > 0 && (
        <div className='absolute right-0 top-full z-50 mt-1 max-h-[min(70vh,32rem)] w-full min-w-[16rem] overflow-auto rounded-lg border border-[#e7e9ef] bg-white p-3 shadow-[0_1px_4px_rgba(38,45,66,0.1),0_1px_4px_rgba(38,45,66,0.05)]'>
          <SearchResults
            term={term}
            debounced={debounced}
            enabled={enabled}
            isError={isError}
            isFetching={isFetching}
            groups={groups}
            onSelect={go}
          />
        </div>
      )}
    </div>
  )
}

export default GlobalSearch
